import React from 'react';
import API from './api';
import './ContractorCompliancePortal.css';

const INVITATION_ERROR = 'This invitation is invalid, expired, or already used. Ask your contractor administrator for a new invitation.';

function invitationParameters() {
  const query = new URLSearchParams(window.location.search);
  return {
    tenantId: query.get('tenant') || '',
    organisationId: query.get('organisation') || '',
    token: query.get('token') || '',
  };
}

function idempotencyKey() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  return `ccv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function ContractorCompliancePortal() {
  const [portalToken, setPortalToken] = React.useState('');
  const [roster, setRoster] = React.useState([]);
  const [state, setState] = React.useState('accepting');
  const [message, setMessage] = React.useState('Checking your secure invitation...');
  const [subjectRef, setSubjectRef] = React.useState('');
  const [kind, setKind] = React.useState('evidence');
  const [note, setNote] = React.useState('');
  const [privacyAccepted, setPrivacyAccepted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const loadRoster = React.useCallback(async (token) => {
    const response = await API.get('/v1/contractor-compliance/portal/roster', {
      headers: { Authorization: `Portal ${token}` }, params: { limit: 250 },
    });
    setRoster(Array.isArray(response.data?.items) ? response.data.items : []);
  }, []);

  React.useEffect(() => {
    let live = true;
    (async () => {
      const invitation = invitationParameters();
      if (!invitation.tenantId || !invitation.organisationId || !invitation.token) {
        if (live) { setState('error'); setMessage(INVITATION_ERROR); }
        return;
      }
      try {
        const response = await API.post('/v1/contractor-compliance/portal/invitations/consume', invitation);
        const token = response.data?.portalToken;
        if (!token) throw new Error('missing portal token');
        window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.hash || ''}`);
        if (!live) return;
        setPortalToken(token);
        await loadRoster(token);
        if (live) { setState('ready'); setMessage('Secure portal ready.'); }
      } catch {
        if (live) { setPortalToken(''); setState('error'); setMessage(INVITATION_ERROR); }
      }
    })();
    return () => { live = false; };
  }, [loadRoster]);

  const submit = async (event) => {
    event.preventDefault();
    if (!portalToken || !privacyAccepted || !subjectRef) return;
    setSubmitting(true);
    setMessage('Submitting metadata securely...');
    try {
      const response = await API.post('/v1/contractor-compliance/portal/submissions', {
        idempotencyKey: idempotencyKey(), kind, subjectRef,
        metadata: { note: note.trim() || undefined },
      }, { headers: { Authorization: `Portal ${portalToken}` } });
      setMessage(`Submission received: ${response.data.submissionId}.`);
      setNote('');
      setPrivacyAccepted(false);
    } catch (error) {
      if (error.response?.status === 401) {
        setPortalToken(''); setState('error');
        setMessage('Your secure portal session has ended. Ask your contractor administrator for a new invitation.');
      } else {
        setMessage('The submission was not accepted. Nothing was discarded; review the details and try again.');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <main className="ccv-portal-shell">
      <section className="ccv-portal-card" aria-labelledby="ccv-portal-title">
        <p className="ccv-portal-kicker">VISINVOKE Contractor Compliance Vault</p>
        <h1 id="ccv-portal-title">Secure contractor portal</h1>
        <p className="ccv-portal-release">NOT RELEASED / COMING SOON / DUE 2027</p>
        <div className={`ccv-portal-status ccv-portal-status--${state}`} role="status" aria-live="polite">{message}</div>
        {state === 'ready' && <>
          <section aria-labelledby="ccv-roster-title">
            <h2 id="ccv-roster-title">Your organisation roster</h2>
            <p>Choose the worker or organisation record the requested evidence belongs to.</p>
            {roster.length ? <ul className="ccv-portal-roster">{roster.map((worker) => (
              <li key={worker.id}><span>{worker.displayName}</span><small>{worker.employerRelationshipState}</small></li>
            ))}</ul> : <p className="ccv-portal-empty">No worker records are currently assigned to this portal session.</p>}
          </section>
          <form className="ccv-portal-form" onSubmit={submit}>
            <h2>Submit requested evidence details</h2>
            <p className="ccv-portal-boundary">This form records submission metadata only. It never marks evidence as verified. Upload and review remain controlled by the authorised evidence workflow.</p>
            <label htmlFor="ccv-subject">Worker or organisation</label>
            <select id="ccv-subject" value={subjectRef} onChange={(event) => setSubjectRef(event.target.value)} required>
              <option value="">Select a record</option><option value="organisation">Own organisation</option>
              {roster.map((worker) => <option key={worker.id} value={worker.id}>{worker.displayName}</option>)}
            </select>
            <label htmlFor="ccv-kind">Submission type</label>
            <select id="ccv-kind" value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="evidence">New evidence</option><option value="resubmission">Requested resubmission</option><option value="declaration">Declaration or acknowledgement</option>
            </select>
            <label htmlFor="ccv-note">Context for the reviewer</label>
            <textarea id="ccv-note" maxLength="2000" value={note} onChange={(event) => setNote(event.target.value)} />
            <div className="ccv-portal-privacy">
              <h3>Purpose and retention</h3>
              <p>Submit only information requested for contractor readiness. Your customer controls who may review it, applies its configured retention policy, and may preserve decision history or a lawful hold. Supervisors receive status-only views unless separately authorised.</p>
              <label className="ccv-portal-consent"><input type="checkbox" checked={privacyAccepted} onChange={(event) => setPrivacyAccepted(event.target.checked)} />I understand the purpose and retention information above.</label>
            </div>
            <button type="submit" disabled={submitting || !privacyAccepted || !subjectRef}>{submitting ? 'Submitting...' : 'Submit securely'}</button>
          </form>
        </>}
      </section>
    </main>
  );
}

export { INVITATION_ERROR };
